import { useQuery } from "@tanstack/react-query";
import { FolderTree } from "lucide-react";
import type { FC, ReactNode } from "react";
import React from "react";

import { bindArgs } from "@core/foundation/function";

import { ErrorView, LoadingView } from "@react/foundation/placeholder";
import { Button } from "@react/ui-primitives/button";

import type { Taxonomy } from "@apis/taxonomy/models";

import { PageView } from "../../../shared/components/PageView.tsx";
import { useQueries } from "../../../shared/hooks/useQueries.ts";
import { useMediaActions } from "../hooks/useMediaActions.ts";

interface MediaViewProps {
  readonly renderFolderTree: (taxonomy: Taxonomy) => ReactNode;
}

export const MediaView: FC<MediaViewProps> = ({ renderFolderTree }) => {
  const { getMediaTaxonomiesQueryOptions } = useQueries();
  const taxonomies = useQuery(getMediaTaxonomiesQueryOptions());

  const { createTaxonomy } = useMediaActions();

  if (taxonomies.status === "pending") {
    return <LoadingView />;
  }

  if (taxonomies.status === "error") {
    return <ErrorView description="Failed to load taxonomies." />;
  }

  return (
    <PageView
      icon={<FolderTree />}
      subtitle="The taxonomies your agents classify your media with."
      title="Media"
    >
      {taxonomies.data.items.length === 0 ? (
        <Button
          disabled={createTaxonomy.isPending}
          onClick={bindArgs(createTaxonomy.mutate, { name: "Default" })}
        >
          {createTaxonomy.isPending
            ? "Creating…"
            : "Create your first taxonomy"}
        </Button>
      ) : (
        <ul className="flex flex-col gap-4">
          {taxonomies.data.items.map(taxonomy => (
            <li
              className="flex flex-col gap-3 rounded-md border p-3"
              key={taxonomy.id}
            >
              <span className="font-medium">{taxonomy.name}</span>
              {renderFolderTree(taxonomy)}
            </li>
          ))}
        </ul>
      )}
    </PageView>
  );
};
